import React, { useState, useEffect } from "react";
import axios from "axios";
import LoadingIcon from "@/components/LoadingIcon";
import { useRouter } from "next/router";
import {
  AcademicCapIcon,
  BuildingOffice2Icon,
  CogIcon,
  CubeTransparentIcon,
  DocumentCurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import moment from "moment";
import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import Link from "next/link";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css"; // optional
import ShowModulesPanel from "@/components/ShowModulesPanel";
import PlanCard from "@/components/company/PlanCard";
import SettingsSidebar from "@/navigation/company";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

export async function getServerSideProps(context) {
  const { locale } = context;

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        "dashboard/common",
        "dashboard/navbar",
      ])),
    },
  };
}

function Company() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [subscription, setSubscription] = useState();
  const [openModules, setOpenModules] = useState(false);

  const fetchData = async () => {
    try {
      const result = await axios.get("https://backend.kobiworks.com/company");
      const subscriptionData = await axios.get(
        "https://backend.kobiworks.com/company/subscription",
      );

      setSubscription(subscriptionData["data"]);
      setData(result["data"]);
      setLoading(false);
    } catch (err) {
      console.log(err);
      alert("An error has occured.");
    }
  };

  const update = async () => {
    try {
      const result = await axios.post(
        "https://backend.kobiworks.com/company/update",
        data,
      );

      if (result) {
        Toastify({
          text: `Saved successfully.`,
          duration: 2000,
          style: {
            background: "#111827",
            borderRadius: "5px",
          },
          className: "shadow-xl border-none rounded-lg",
          position: "right",
          stopOnFocus: false,
        }).showToast();
      }
    } catch {
      alert("An error has occured.");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUploadImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result.split(",")[1];

        const PROCESSED_DATA = await axios.post(
          "https://backend.kobiworks.com/upload",
          {
            fileName: file["name"],
            fileData: base64String,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const { url, fileName } = PROCESSED_DATA["data"];

        Toastify({
          text: `Image Uploaded: ${fileName}`,
          duration: 2000,
          style: {
            background: "#111827",
            borderRadius: "5px",
          },
          className: "shadow-xl border-none rounded-lg",
          position: "right",
          stopOnFocus: false,
        }).showToast();

        setData({ ...data, imageUrl: "https://backend.kobiworks.com" + url });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex">
      <div className="min-h-screen w-full">
        {loading ? (
          <LoadingIcon />
        ) : (
          <div className={`mx-auto fade-in`}>
            <div className="flex h-full">
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <ShowModulesPanel open={openModules} setOpen={setOpenModules} />

                <main className="flex flex-1 overflow-hidden">
                  <div className="flex flex-1 flex-col overflow-y-auto xl:overflow-hidden">
                    {/* Breadcrumb */}
                    <nav
                      aria-label="Breadcrumb"
                      className="border-b border-slate-200 bg-white xl:hidden"
                    >
                      <div className="mx-auto flex max-w-3xl items-start px-4 py-3 sm:px-6 lg:px-8">
                        <a
                          href="#"
                          className="-ml-1 inline-flex items-center space-x-3 text-sm font-medium text-slate-900"
                        >
                          <ChevronLeftIcon
                            aria-hidden="true"
                            className="h-5 w-5 text-slate-400"
                          />
                          <span>Settings</span>
                        </a>
                      </div>
                    </nav>

                    <div className="flex flex-1 xl:overflow-hidden">
                      <SettingsSidebar />

                      <div className="flex-1 xl:overflow-y-auto">
                        <div className="mx-auto px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
                          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                            Company Details
                          </h1>

                          <div className="divide-y-slate-200 mt-6 space-y-8 divide-y">
                            <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
                              <div className="sm:col-span-6">
                                <h2 className="text-xl font-medium text-slate-900">
                                  Profile
                                </h2>
                                <p className="mt-1 text-sm text-slate-500">
                                  Company details some of them may not be able
                                  to be changed if there are already existing
                                  values.
                                </p>
                              </div>

                              <Tippy content="If you'd like to change your company name contact an admin!">
                                <div className="sm:col-span-6">
                                  <label
                                    htmlFor="first-name"
                                    className="block text-sm font-medium leading-6 text-slate-900"
                                  >
                                    Company Name
                                  </label>
                                  <input
                                    value={data["title"]}
                                    // onChange={(e) => setData({...data, title: e.target.value})}
                                    disabled
                                    id="first-name"
                                    name="first-name"
                                    type="text"
                                    autoComplete="given-name"
                                    className="mt-2 px-4 block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                                  />
                                </div>
                              </Tippy>

                              <div className="sm:col-span-6">
                                <label
                                  htmlFor="photo"
                                  className="block text-sm font-medium leading-6 text-slate-900"
                                >
                                  Photo
                                </label>
                                <div className="mt-2 flex items-center">
                                  <img
                                    alt=""
                                    src={
                                      data["imageUrl"] ? data["imageUrl"] : ""
                                    }
                                    className="inline-block object-contain h-12 w-12 rounded-full"
                                  />
                                  <div className="relative ml-4">
                                    <input
                                      onChange={handleUploadImage}
                                      id="user-photo"
                                      name="user-photo"
                                      type="file"
                                      className="peer absolute inset-0 h-full w-full rounded-md opacity-0"
                                    />
                                    <label
                                      htmlFor="user-photo"
                                      className="pointer-events-none block rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 peer-hover:bg-slate-50 peer-focus:ring-2 peer-focus:ring-blue-600"
                                    >
                                      <span>Change</span>
                                      <span className="sr-only">
                                        {" "}
                                        user photo
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              </div>

                              <div className="sm:col-span-6 grid sm:grid-cols-6 gap-4">
                                <div className="col-span-full">
                                  <label
                                    htmlFor="street-address"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    Street address
                                  </label>
                                  <div className="mt-2">
                                    <input
                                      value={data["billingAddress"]}
                                      onChange={(e) =>
                                        setData({
                                          ...data,
                                          billingAddress: e.target.value,
                                        })
                                      }
                                      className="block w-full px-4 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    />
                                  </div>
                                </div>
                                {/* 
                                <div className="sm:col-span-2 sm:col-start-1">
                                  <label
                                    htmlFor="city"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    City
                                  </label>
                                  <div className="mt-2">
                                    <input className="block w-full px-4 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                                  </div>
                                </div>

                                <div className="sm:col-span-2">
                                  <label
                                    htmlFor="region"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    State / Province
                                  </label>
                                  <div className="mt-2">
                                    <input className="block w-full px-4 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                                  </div>
                                </div>

                                <div className="sm:col-span-2">
                                  <label
                                    htmlFor="postal-code"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                  >
                                    ZIP / Postal code
                                  </label>
                                  <div className="mt-2">
                                    <input className="block w-full px-4 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                                  </div>
                                </div> */}
                              </div>

                              <div className="sm:col-span-6">
                                <label
                                  htmlFor="url"
                                  className="block text-sm font-medium leading-6 text-slate-900"
                                >
                                  Website URL
                                </label>
                                <input
                                  value={data["website"]}
                                  onChange={(e) =>
                                    setData({
                                      ...data,
                                      website: e.target.value,
                                    })
                                  }
                                  id="url"
                                  name="url"
                                  type="text"
                                  className="mt-2 block px-4 w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                                />
                              </div>

                              <div className="sm:col-span-6">
                                <label
                                  htmlFor="url"
                                  className="block text-sm font-medium leading-6 text-slate-900"
                                >
                                  Company Phone Number
                                </label>
                                <input
                                  value={data["phoneNumber"]}
                                  onChange={(e) =>
                                    setData({
                                      ...data,
                                      phoneNumber: e.target.value,
                                    })
                                  }
                                  id="url"
                                  name="url"
                                  type="text"
                                  className="mt-2 block w-full rounded-md px-4 border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                                />
                              </div>
                              <div className="sm:col-span-6">
                                <label
                                  htmlFor="url"
                                  className="block text-sm font-medium leading-6 text-slate-900"
                                >
                                  Company Email
                                </label>
                                <input
                                  value={data["email"]}
                                  onChange={(e) =>
                                    setData({ ...data, email: e.target.value })
                                  }
                                  id="url"
                                  name="url"
                                  type="text"
                                  className="mt-2 block w-full rounded-md px-4 border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                                />
                              </div>

                              <p className="text-sm text-slate-500 sm:col-span-6">
                                This company was created on{" "}
                                {data
                                  ? moment(data["timestamp"]).format("lll")
                                  : ""}
                              </p>
                            </div>

                            <div className="flex justify-end gap-x-3 pt-8">
                              <button
                                onClick={() => update()}
                                className="inline-flex justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                              >
                                Save
                              </button>
                            </div>
                          </div>

                          {/* Plan, usage meters, dunning banner, pending
                              downgrade and SMS usage. Everything is read from
                              GET /company/entitlements + /company/sms-usage —
                              no price, cap or feature list is hardcoded here,
                              so the UI can never promise something the server
                              does not enforce. Replaces the old hardcoded
                              "$19.99 / $299" tiles and the trial callout. */}
                          <div className="mt-10 border-t border-[#e4e4e7] pt-8">
                            <PlanCard />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Company;
